import {
    Box,
    Checkbox,
    VStack,
    HStack,
    Text,
    Icon,
    Flex,
    Tr,
    Td,
    InputGroup,
    InputLeftElement,
    Input,
    Badge,
    AlertDialog,
    AlertDialogBody,
    AlertDialogCloseButton,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    useToast,
} from '@chakra-ui/react';
import { useState, useMemo, useCallback, useContext, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { TbBriefcase, TbChevronDown, TbMapPin, TbSearch, TbUserCheck } from 'react-icons/tb';
import SortArrowIcon, { SortIconDirection } from '../SortArrowIcon';
import { UserEntry, UserStatus, getUserStatus, useGetSettings } from '../../hooks/useGetSettings';
import { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import Pagination from '../Pagination';
import PartBayButton from '../PartBayButton';
import { useGetLocations, type LocationEntry } from '../../hooks/useGetLocations';
import SessionContext, { isInventoryOrgSession } from '../../contexts/SessionContext';
import { useDeleteUserInvite } from '../../hooks/useDeleteUserInvite';
import { useEditUser, type EditUserInput } from '../../hooks/useEditUser';
import { POSITION_LABELS, getInvitePositionOptions } from '../../utils/invitePositionOptions';
import { isInternalEmail } from '../../utils/internalAccess';
import type { Role } from '../../hooks/useValidateSession';
import UsersAdvancedFilters, { UsersListFilters } from './UsersAdvancedFilters';
import BulkActionBar, { type BulkActionBarItem } from '../BulkActionBar';
import UsersBulkEditModal, {
    type UsersBulkField,
    type UsersBulkValue,
} from './UsersBulkEditModal';
import {
    InlineLocationsCell,
    InlineSelectCell,
    formatLocations,
    isSameLocationValue,
    type InlineCellOption,
} from './UsersInlineCells';
import {
    SettingsActionBar,
    SettingsEmptyState,
    SettingsInlineEdit,
    SettingsOptionRow,
    SettingsPageActions,
    SettingsRowActions,
    SettingsTable,
    SettingsTableFooter,
    SettingsTd,
    SettingsTh,
    useDynamicPageSize,
} from './primitives';
import {
    PARTBAY_BLUE,
    SETTINGS_TABLE_GUTTER,
} from './primitives/constants';

interface UsersInterface {
    users: Array<UserEntry>;
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
}

/** Status is only active/inactive; admin now lives in its own Role column. */
type EditableStatusValue = 'active' | 'inactive';

const STATUS_EDIT_OPTIONS: Array<{ value: EditableStatusValue; label: string }> = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
];

/**
 * Everyone who is not an admin is a Member. There is no stored "member" role —
 * it is just `admin: false`, so this stays a display concept.
 */
type UserRoleValue = 'admin' | 'member';

const ROLE_EDIT_OPTIONS: InlineCellOption[] = [
    { value: 'admin', label: 'Admin' },
    { value: 'member', label: 'Member' },
];

/**
 * Status badge for a row. Pending invites have no editable state; everything
 * else reads the row draft so an unsaved flip shows straight away. Admin is not
 * a status here — the Role column owns it.
 */
const statusBadgeProps = (
    isPending: boolean,
    isActive: boolean,
): { colorScheme: string; label: string } => {
    if (isPending) return { colorScheme: 'orange', label: 'Pending' };
    return isActive
        ? { colorScheme: 'green', label: 'Active' }
        : { colorScheme: 'gray', label: 'Inactive' };
};

/**
 * What the Permissions cell reads, matching the badge on the user detail page:
 * admins have everything, anyone else either follows their role defaults or
 * carries a saved custom set. `isAdmin` comes from the row draft so the cell
 * tracks an unsaved role change.
 */
const permissionsLabel = (user: UserEntry, isAdmin: boolean): string => {
    if (isAdmin) return 'Admin';
    // Demoting an admin sends permissions back to the role defaults, so a
    // stored 'admin' set reads as Default the moment the role is staged down.
    if (user.permissions === 'default' || user.permissions === 'admin' || user.permissions == null) {
        return 'Default';
    }
    return 'Custom';
};

/**
 * The editable half of a row. Inline and bulk edits write here instead of
 * PATCHing immediately, so the whole table saves through one action bar the way
 * every other settings section does.
 */
interface UserRowDraft {
    active: boolean;
    admin: boolean;
    position: Role;
    locationId: Array<string> | 'all';
}

/** Prefer `status` over the legacy `active` flag, same as getUserStatus. */
const savedActive = (user: UserEntry): boolean => getUserStatus(user) !== 'inactive';

const buildRowDraft = (user: UserEntry): UserRowDraft => ({
    active: savedActive(user),
    admin: user.admin === true,
    position: user.position,
    locationId: user.locationId || 'all',
});

const sameRowDraft = (a: UserRowDraft, b: UserRowDraft): boolean =>
    a.active === b.active &&
    a.admin === b.admin &&
    a.position === b.position &&
    isSameLocationValue(a.locationId, b.locationId);

/** Only changed fields go out, matching the retired per-cell PATCHes. */
const buildEditPayload = (user: UserEntry, draft: UserRowDraft): EditUserInput => {
    const payload: EditUserInput = { userId: user._id };
    if (draft.active !== savedActive(user)) payload.active = draft.active;
    if (draft.admin !== (user.admin === true)) {
        payload.admin = draft.admin;
        // Admin is admin:true + permissions:'admin'; Member drops back to the
        // role defaults, exactly as the old inline Role editor wrote it.
        payload.permissions = draft.admin ? 'admin' : 'default';
    }
    if (draft.position !== user.position) payload.position = draft.position;
    if (!isSameLocationValue(user.locationId || 'all', draft.locationId)) {
        payload.locationId = draft.locationId;
    }
    return payload;
};

/** Bulk bar actions; each opens the shared value picker for that field. */
const BULK_ACTIONS: BulkActionBarItem[] = [
    { id: 'status', label: 'Status', icon: TbUserCheck as React.ElementType },
    { id: 'position', label: 'Position', icon: TbBriefcase as React.ElementType },
    { id: 'locations', label: 'Locations', icon: TbMapPin as React.ElementType },
];

const displayName = (user: UserEntry): string => {
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return name || '—';
};

type UsersSortField =
    | 'name'
    | 'email'
    | 'role'
    | 'permissions'
    | 'location'
    | 'position'
    | 'status';

type UsersSortDirection = 'asc' | 'desc';

const EMPTY_SORT_VALUES = new Set(['', '—', 'N/A', 'None']);

const DEFAULT_STATUS_ORDER: Record<UserStatus, number> = {
    active: 0,
    pending: 1,
    inactive: 2,
};

const compareSortValues = (a: string, b: string, direction: UsersSortDirection): number => {
    const aEmpty = EMPTY_SORT_VALUES.has(a);
    const bEmpty = EMPTY_SORT_VALUES.has(b);
    if (aEmpty && !bEmpty) return 1;
    if (bEmpty && !aEmpty) return -1;
    const comparison = a.localeCompare(b, undefined, { sensitivity: 'base' });
    return direction === 'asc' ? comparison : -comparison;
};

/** Join assigned shop titles so multi-location rows sort next to those shops. */
const locationSortKey = (user: UserEntry, locations: LocationEntry[]): string => {
    const value = user.locationId || 'all';
    if (value === 'all') return 'All Locations';
    if (value.length === 0) return 'None';
    const titles = value
        .map((id) => locations.find((location) => location._id === id)?.title)
        .filter((title): title is string => Boolean(title))
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return titles.join(', ') || 'None';
};

const userSortKey = (
    user: UserEntry,
    field: UsersSortField,
    locations: LocationEntry[],
): string => {
    switch (field) {
        case 'name':
            return displayName(user);
        case 'email':
            return user.email || 'N/A';
        case 'role':
            return user.admin === true ? 'Admin' : 'Member';
        case 'permissions':
            return getUserStatus(user) === 'pending'
                ? '—'
                : permissionsLabel(user, user.admin === true);
        case 'location':
            return locationSortKey(user, locations);
        case 'position':
            return POSITION_LABELS[user.position] ?? user.position ?? '—';
        case 'status':
            return getUserStatus(user);
    }
};

const SortableSettingsTh = ({
    field,
    label,
    sortField,
    sortDirection,
    onToggle,
    minW,
}: {
    field: UsersSortField;
    label: string;
    sortField: UsersSortField | null;
    sortDirection: UsersSortDirection | null;
    onToggle: (field: UsersSortField) => void;
    minW?: string;
}) => {
    const isActive = sortField === field && sortDirection !== null;
    const iconDirection: SortIconDirection = !isActive
        ? 'none'
        : sortDirection === 'asc'
            ? 'asc'
            : 'desc';

    return (
        <SettingsTh
            minW={minW}
            color={isActive ? '#005BC4' : undefined}
            cursor="pointer"
            onClick={() => onToggle(field)}
            _hover={{ color: '#005BC4' }}
            userSelect="none"
            transition="color 0.2s"
            aria-sort={
                !isActive ? 'none' : sortDirection === 'asc' ? 'ascending' : 'descending'
            }
        >
            <Flex align="center" gap="6px">
                {label}
                <Box lineHeight={0} color={isActive ? '#005BC4' : 'gray.400'}>
                    <SortArrowIcon direction={iconDirection} size={12} />
                </Box>
            </Flex>
        </SettingsTh>
    );
};

interface InlineUserStatusCellProps {
    display: ReactNode;
    current: EditableStatusValue;
    onSelect: (status: EditableStatusValue) => void;
}

/** Status cell editor: single-select list, saved on selection. */
const InlineUserStatusCell = ({ display, current, onSelect }: InlineUserStatusCellProps) => (
    // Status sits next to the last column, so the panel hangs from the cell's
    // right edge and grows left rather than spilling past the table. That
    // alignment needs a width popper can measure up front, so this one is a
    // fixed panel rather than one matched to the cell.
    <SettingsInlineEdit
        display={display}
        ariaLabel="Edit status"
        placement="bottom-end"
        matchTriggerWidth={false}
        width="160px"
        minW="160px"
    >
        {(close) => (
            <VStack align="stretch" spacing={0}>
                {STATUS_EDIT_OPTIONS.map((option) => {
                    const isSelected = option.value === current;
                    return (
                        <SettingsOptionRow
                            key={option.value}
                            label={option.label}
                            isSelected={isSelected}
                            onToggle={() => {
                                if (!isSelected) onSelect(option.value);
                                close();
                            }}
                        />
                    );
                })}
            </VStack>
        )}
    </SettingsInlineEdit>
);

const userMatchesLocationFilter = (
    user: UserEntry,
    selectedLocationIds: string[],
): boolean => {
    // Org-wide users remain visible when filtering by any specific shop.
    if (user.locationId === 'all') return true;
    if (selectedLocationIds.length === 0) return false;
    const selected = new Set(selectedLocationIds);
    return user.locationId.some((id) => selected.has(id));
};

const Users = ({ users, refetch }: UsersInterface) => {
    const sessionContext = useContext(SessionContext);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [listFilters, setListFilters] = useState<UsersListFilters>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState<UsersSortField | null>(null);
    const [sortDirection, setSortDirection] = useState<UsersSortDirection | null>(null);
    const [inviteToDelete, setInviteToDelete] = useState<UserEntry | null>(null);
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
    const [bulkField, setBulkField] = useState<UsersBulkField | null>(null);
    /** Staged row edits, keyed by user id. Absent means untouched. */
    const [rowDrafts, setRowDrafts] = useState<Record<string, UserRowDraft>>({});
    const [isSavingEdits, setIsSavingEdits] = useState(false);
    const deleteInviteCancelRef = useRef<HTMLButtonElement>(null);
    const toast = useToast();
    const deleteInviteMutation = useDeleteUserInvite();
    const editUserMutation = useEditUser();
    const { scrollRef: tableScrollRef, pageSize: usersPerPage } = useDynamicPageSize();
    const { data: locationsData } = useGetLocations();
    const { data: settingsData } = useGetSettings();

    // Hide Yaxle staff accounts from the org Users list (the API already excludes them).
    const orgUsers = useMemo(
        () => users.filter((user) => !isInternalEmail(user.email)),
        [users]
    );

    const isInventoryOrg = isInventoryOrgSession(sessionContext);
    const positionOptions = useMemo(
        () =>
            getInvitePositionOptions({
                includeTechnician: settingsData?.inventoryEnabled === true,
                includeInvoicing: settingsData?.invoicingEnabled === true,
                inventoryOrg: isInventoryOrg,
            }),
        [isInventoryOrg, settingsData?.inventoryEnabled, settingsData?.invoicingEnabled],
    );

    // Drop selected positions that are no longer offered (e.g. feature gate turned off).
    useEffect(() => {
        if (!listFilters.positions?.length) return;
        const valid = new Set(positionOptions.map((o) => o.value));
        const next = listFilters.positions.filter((p) => valid.has(p));
        if (next.length === listFilters.positions.length) return;
        setListFilters((prev) => {
            const updated = { ...prev };
            if (next.length > 0) updated.positions = next;
            else delete updated.positions;
            return updated;
        });
    }, [positionOptions, listFilters.positions]);

    const filteredUsers = useMemo(() => {
        let result = orgUsers;

        if ((listFilters.statuses?.length ?? 0) > 0) {
            const selected = new Set(listFilters.statuses);
            result = result.filter((user) => selected.has(getUserStatus(user)));
        }

        if ((listFilters.locationIds?.length ?? 0) > 0) {
            result = result.filter((user) =>
                userMatchesLocationFilter(user, listFilters.locationIds ?? []),
            );
        }

        if ((listFilters.positions?.length ?? 0) > 0) {
            const selected = new Set(listFilters.positions);
            result = result.filter((user) => selected.has(user.position));
        }

        if (searchTerm) {
            const query = searchTerm.toLowerCase();
            result = result.filter(user => {
                const firstNameCheck = (user.firstName ?? '').toLowerCase().startsWith(query);
                const lastNameCheck = (user.lastName ?? '').toLowerCase().startsWith(query);
                const emailCheck = user.email.toLowerCase().startsWith(query);
                const usernameCheck = (user.username ?? '').toLowerCase().startsWith(query);
                return firstNameCheck || lastNameCheck || emailCheck || usernameCheck;
            });
        }

        if (sortField && sortDirection) {
            const locations = locationsData?.locations ?? [];
            result = [...result].sort((a, b) =>
                compareSortValues(
                    userSortKey(a, sortField, locations),
                    userSortKey(b, sortField, locations),
                    sortDirection,
                ),
            );
        } else {
            result = [...result].sort(
                (a, b) => DEFAULT_STATUS_ORDER[getUserStatus(a)] - DEFAULT_STATUS_ORDER[getUserStatus(b)]
            );
        }

        return result;
    }, [orgUsers, searchTerm, listFilters, sortField, sortDirection, locationsData?.locations]);

    const totalPages = useMemo(() => Math.ceil(filteredUsers.length / usersPerPage), [filteredUsers.length, usersPerPage]);

    // The page size follows the viewport, so a resize can shrink the page
    // count; clamp so the user is never stranded on a now-empty page.
    useEffect(() => {
        setCurrentPage((prev) => Math.min(prev, Math.max(1, totalPages)));
    }, [totalPages]);

    const currentUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * usersPerPage;
        return filteredUsers.slice(startIndex, startIndex + usersPerPage);
    }, [filteredUsers, currentPage, usersPerPage]);

    // Pending invites have no user document to patch and other admins are
    // view-only, so neither can be edited inline nor selected for a bulk change.
    // Yaxle employees override the admin lock so support can manage any user.
    const isUserEditable = useCallback(
        (user: UserEntry) =>
            getUserStatus(user) !== 'pending' &&
            (!user.admin ||
                user._id === sessionContext?.userId ||
                sessionContext?.isInternal === true),
        [sessionContext?.userId, sessionContext?.isInternal],
    );

    // Search and filters can hide a selected row; drop those ids so the bulk
    // bar never acts on users who are no longer on screen.
    useEffect(() => {
        setSelectedUserIds((prev) => {
            if (prev.size === 0) return prev;
            const visible = new Set(filteredUsers.map((user) => user._id));
            const next = new Set(Array.from(prev).filter((id) => visible.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [filteredUsers]);

    const selectablePageUsers = useMemo(
        () => currentUsers.filter(isUserEditable),
        [currentUsers, isUserEditable],
    );

    const allPageSelected =
        selectablePageUsers.length > 0 &&
        selectablePageUsers.every((user) => selectedUserIds.has(user._id));
    const somePageSelected = selectablePageUsers.some((user) => selectedUserIds.has(user._id));

    const selectedUsers = useMemo(
        () => orgUsers.filter((user) => selectedUserIds.has(user._id) && isUserEditable(user)),
        [orgUsers, selectedUserIds, isUserEditable],
    );

    const handleToggleSelect = (userId: string, checked: boolean) => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(userId);
            else next.delete(userId);
            return next;
        });
    };

    // Header checkbox covers the current page only, matching what is visible.
    const handleSelectAllOnPage = (checked: boolean) => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            selectablePageUsers.forEach((user) => {
                if (checked) next.add(user._id);
                else next.delete(user._id);
            });
            return next;
        });
    };

    /**
     * A bulk change stages the same row drafts an inline edit would, so both
     * kinds of edit leave through the one action bar. Users already holding the
     * chosen value are skipped so nothing shows as dirty for no reason.
     */
    const handleBulkApply = (value: UsersBulkValue) => {
        const next = { ...rowDrafts };
        let changed = 0;
        selectedUsers.forEach((user) => {
            const saved = buildRowDraft(user);
            const merged = { ...(next[user._id] ?? saved) };
            if (value.active !== undefined) merged.active = value.active;
            if (value.position !== undefined) merged.position = value.position;
            if (value.locationId !== undefined) merged.locationId = value.locationId;
            if (sameRowDraft(merged, saved)) {
                delete next[user._id];
            } else {
                next[user._id] = merged;
                changed += 1;
            }
        });

        if (changed === 0) {
            toast({
                title: 'No changes to apply',
                description: 'The selected users already have that value.',
                status: 'info',
                duration: 4000,
                isClosable: true,
            });
            setBulkField(null);
            return;
        }

        setRowDrafts(next);
        setBulkField(null);
        setSelectedUserIds(new Set());
    };

    // Keep a user's current position selectable (and correctly labelled) even
    // when its feature gate is off, so the cell never reads as unset.
    const positionOptionsFor = (user: UserEntry): InlineCellOption[] =>
        positionOptions.some((option) => option.value === user.position)
            ? positionOptions
            : [
                  ...positionOptions,
                  {
                      value: user.position,
                      label: POSITION_LABELS[user.position] ?? user.position,
                  },
              ];

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleFiltersChange = (next: UsersListFilters) => {
        setListFilters(next);
        setCurrentPage(1);
    };

    const handleSortToggle = (field: UsersSortField) => {
        if (sortField !== field) {
            setSortField(field);
            setSortDirection('asc');
        } else if (sortDirection === 'asc') {
            setSortDirection('desc');
        } else {
            setSortField(null);
            setSortDirection(null);
        }
        setCurrentPage(1);
    };

    /**
     * Stages one field of a row. Editing a value back to what the server holds
     * drops the row again, so the action bar disappears on its own instead of
     * offering to save a no-op.
     */
    const stageRowEdit = (user: UserEntry, patch: Partial<UserRowDraft>) => {
        setRowDrafts((prev) => {
            const saved = buildRowDraft(user);
            const next = { ...(prev[user._id] ?? saved), ...patch };
            const { [user._id]: _dropped, ...rest } = prev;
            return sameRowDraft(next, saved) ? rest : { ...rest, [user._id]: next };
        });
    };

    /** Status now only flips `active`; the Role column owns the admin flag. */
    const handleStatusSelect = (user: UserEntry, next: EditableStatusValue) => {
        stageRowEdit(user, { active: next === 'active' });
    };

    const handleRoleSelect = (user: UserEntry, next: UserRoleValue) => {
        stageRowEdit(user, { admin: next === 'admin' });
    };

    const handlePositionSelect = (user: UserEntry, next: Role) => {
        stageRowEdit(user, { position: next });
    };

    const handleLocationsSave = (user: UserEntry, next: Array<string> | 'all') => {
        stageRowEdit(user, { locationId: next });
    };

    const dirtyUserIds = Object.keys(rowDrafts);

    /**
     * There is no bulk endpoint, so a save is one PATCH /settings/user/edit per dirty
     * row, fired together. Rows that fail stay staged so they can be retried.
     */
    const handleSaveEdits = async () => {
        const payloads = dirtyUserIds
            .map((userId) => {
                const user = orgUsers.find((candidate) => candidate._id === userId);
                const draft = rowDrafts[userId];
                return user && draft ? buildEditPayload(user, draft) : null;
            })
            // Only userId means nothing actually differs any more.
            .filter((payload): payload is EditUserInput => payload !== null && Object.keys(payload).length > 1);

        if (payloads.length === 0) {
            setRowDrafts({});
            return;
        }

        setIsSavingEdits(true);
        const results = await Promise.allSettled(
            payloads.map((payload) => editUserMutation.mutateAsync(payload)),
        );
        setIsSavingEdits(false);

        const failedIds = payloads
            .filter((_, index) => results[index].status === 'rejected')
            .map((payload) => payload.userId);
        const saved = payloads.length - failedIds.length;

        toast({
            title:
                failedIds.length === 0
                    ? `${saved} user${saved === 1 ? '' : 's'} updated`
                    : 'Some updates failed',
            description:
                failedIds.length === 0
                    ? undefined
                    : `${saved} updated, ${failedIds.length} could not be saved. Please try again.`,
            status: failedIds.length === 0 ? 'success' : 'error',
            duration: 5000,
            isClosable: true,
        });

        setRowDrafts((prev) =>
            Object.fromEntries(failedIds.filter((id) => prev[id]).map((id) => [id, prev[id]])),
        );
        refetch();
    };

    const handleConfirmDeleteInvite = () => {
        const inviteId = inviteToDelete?.inviteId ?? inviteToDelete?._id;
        if (!inviteId) return;

        deleteInviteMutation.mutate(
            { inviteId },
            {
                onSuccess: () => {
                    toast({
                        title: 'Invitation deleted',
                        description: 'The pending user has been removed.',
                        status: 'success',
                        duration: 5000,
                        isClosable: true
                    });
                    setInviteToDelete(null);
                    refetch();
                },
                onError: (error: any) => {
                    toast({
                        title: 'Error deleting invitation',
                        description: error?.response?.data?.message || 'Failed to delete invitation. Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true
                    });
                }
            }
        );
    };

    if (orgUsers.length === 0) {
        return (
            <SettingsEmptyState
                icon="users"
                title="No users invited yet"
                description="Invite your first teammate to get started"
                action={{
                    label: 'Add Users',
                    onClick: () => navigate('/settings/users/new'),
                }}
            />
        );
    }

    return (
        <>
            <SettingsPageActions>
                <PartBayButton
                    size="sm"
                    variant="solid"
                    colorScheme="partbay-blue"
                    onClick={() => navigate('/settings/users/new')}
                    text="Add Users"
                />
            </SettingsPageActions>

            <HStack
                justify="space-between"
                align="center"
                gap={4}
                px={SETTINGS_TABLE_GUTTER}
                pb={4}
                flexShrink={0}
            >
                <InputGroup size="sm" maxW="500px">
                    <InputLeftElement>
                        <Icon as={TbSearch as React.ElementType} color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search by name, email, or username..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        bg="white"
                    />
                </InputGroup>
                <HStack spacing={2} flexShrink={0}>
                    <UsersAdvancedFilters
                        filters={listFilters}
                        onFiltersChange={handleFiltersChange}
                        locations={locationsData?.locations ?? []}
                        positionOptions={positionOptions}
                    />
                </HStack>
            </HStack>

            <SettingsTable
                scrollRef={tableScrollRef}
                head={
                    <>
                        <SettingsTh w="44px">
                            <Checkbox
                                size="sm"
                                colorScheme="partbay-blue"
                                isChecked={allPageSelected}
                                isIndeterminate={!allPageSelected && somePageSelected}
                                isDisabled={selectablePageUsers.length === 0}
                                onChange={(e) => handleSelectAllOnPage(e.target.checked)}
                                aria-label="Select all users on this page"
                            />
                        </SettingsTh>
                        <SortableSettingsTh
                            field="name"
                            label="Name"
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onToggle={handleSortToggle}
                        />
                        <SortableSettingsTh
                            field="email"
                            label="Email"
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onToggle={handleSortToggle}
                        />
                        <SortableSettingsTh
                            field="role"
                            label="Role"
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onToggle={handleSortToggle}
                            minW="130px"
                        />
                        <SortableSettingsTh
                            field="permissions"
                            label="Permissions"
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onToggle={handleSortToggle}
                            minW="140px"
                        />
                        <SortableSettingsTh
                            field="location"
                            label="Locations"
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onToggle={handleSortToggle}
                            minW="180px"
                        />
                        <SortableSettingsTh
                            field="position"
                            label="Position"
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onToggle={handleSortToggle}
                            minW="170px"
                        />
                        <SortableSettingsTh
                            field="status"
                            label="Status"
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onToggle={handleSortToggle}
                            minW="120px"
                        />
                        <SettingsTh textAlign="center">ACTIONS</SettingsTh>
                    </>
                }
            >
                {currentUsers.length === 0 ? (
                    <Tr>
                        <Td colSpan={9} py={10} textAlign="center">
                            <Text fontSize="sm" color="gray.500">
                                No users match your filters
                            </Text>
                        </Td>
                    </Tr>
                ) : (
                    currentUsers.map((user) => {
                        const isPending = getUserStatus(user) === 'pending';
                        // Every editable cell reads the staged draft, so an
                        // unsaved change shows in the row before it is saved.
                        const draft = rowDrafts[user._id] ?? buildRowDraft(user);
                        const badge = statusBadgeProps(isPending, draft.active);
                        // `canEdit` stays on the saved row: staging someone up
                        // to Admin must not lock the cell that undoes it.
                        const canEdit = isUserEditable(user);
                        // Position and locations mean nothing on a deactivated
                        // account, matching the user detail page.
                        const canEditAccess = canEdit && !isPending && draft.active;
                        const locationValue = draft.locationId;

                        const statusBadge = (
                            <Badge colorScheme={badge.colorScheme} variant="subtle">
                                {badge.label}
                            </Badge>
                        );
                        // Editable badge cells get the same row-hover chevron as
                        // the text dropdowns, so they read as pickers too.
                        const statusDisplay = (
                            <HStack spacing={2} justify="space-between" w="100%">
                                {statusBadge}
                                <Icon
                                    as={TbChevronDown as React.ElementType}
                                    boxSize={3.5}
                                    color="gray.400"
                                    flexShrink={0}
                                    opacity={0}
                                    transition="opacity 0.15s"
                                    _groupHover={{ opacity: 1 }}
                                />
                            </HStack>
                        );

                        return (
                            // role="group" powers the chevron that fades in on
                            // hover for every editable cell in the row.
                            <Tr key={user._id} role="group" _hover={{ bg: "gray.25" }}>
                                <SettingsTd>
                                    <Checkbox
                                        size="sm"
                                        colorScheme="partbay-blue"
                                        isChecked={selectedUserIds.has(user._id)}
                                        isDisabled={!canEdit}
                                        onChange={(e) => handleToggleSelect(user._id, e.target.checked)}
                                        aria-label={`Select ${displayName(user)}`}
                                    />
                                </SettingsTd>
                                <SettingsTd>
                                    {isPending ? (
                                        // Pending invites have no user document, so no detail page.
                                        <Text fontSize="sm" color="gray.400">
                                            {displayName(user)}
                                        </Text>
                                    ) : (
                                        <Text
                                            as="button"
                                            fontSize="sm"
                                            textAlign="left"
                                            color="gray.800"
                                            _hover={{ color: PARTBAY_BLUE, textDecoration: 'underline' }}
                                            onClick={() => navigate(`/settings/users/${user._id}`)}
                                        >
                                            {displayName(user)}
                                        </Text>
                                    )}
                                </SettingsTd>
                                <SettingsTd>
                                    <Text fontSize="sm" color="gray.600">
                                        {user.email || 'N/A'}
                                    </Text>
                                </SettingsTd>
                                <SettingsTd>
                                    <InlineSelectCell
                                        value={draft.admin ? 'admin' : 'member'}
                                        options={ROLE_EDIT_OPTIONS}
                                        onSelect={(next) => handleRoleSelect(user, next as UserRoleValue)}
                                        isDisabled={!canEdit}
                                        ariaLabel="Edit role"
                                        searchPlaceholder="Search roles"
                                        minW="180px"
                                    />
                                </SettingsTd>
                                <SettingsTd>
                                    {isPending ? (
                                        <Text fontSize="sm" color="gray.400">
                                            —
                                        </Text>
                                    ) : (
                                        // The matrix itself lives on the user page.
                                        <Text
                                            as="button"
                                            fontSize="sm"
                                            textAlign="left"
                                            color="gray.700"
                                            _hover={{ color: PARTBAY_BLUE, textDecoration: 'underline' }}
                                            onClick={() =>
                                                navigate(`/settings/users/${user._id}`, {
                                                    state: { focus: 'permissions' },
                                                })
                                            }
                                        >
                                            {permissionsLabel(user, draft.admin)}
                                        </Text>
                                    )}
                                </SettingsTd>
                                <SettingsTd>
                                    {canEditAccess ? (
                                        <InlineLocationsCell
                                            value={locationValue}
                                            locations={locationsData?.locations ?? []}
                                            onSave={(next) => handleLocationsSave(user, next)}
                                        />
                                    ) : (
                                        <Text fontSize="sm" color="gray.600" noOfLines={1}>
                                            {formatLocations(locationValue, locationsData?.locations ?? [])}
                                        </Text>
                                    )}
                                </SettingsTd>
                                <SettingsTd>
                                    {canEditAccess ? (
                                        <InlineSelectCell
                                            value={draft.position}
                                            options={positionOptionsFor(user)}
                                            onSelect={(next) => handlePositionSelect(user, next as Role)}
                                            ariaLabel="Edit position"
                                            searchPlaceholder="Search positions"
                                        />
                                    ) : (
                                        <Text fontSize="sm" color="gray.600" noOfLines={1}>
                                            {POSITION_LABELS[user.position] ?? user.position ?? '—'}
                                        </Text>
                                    )}
                                </SettingsTd>
                                <SettingsTd>
                                    {canEdit ? (
                                        <InlineUserStatusCell
                                            display={statusDisplay}
                                            current={draft.active ? 'active' : 'inactive'}
                                            onSelect={(next) => handleStatusSelect(user, next)}
                                        />
                                    ) : (
                                        statusBadge
                                    )}
                                </SettingsTd>
                                <SettingsTd textAlign="center">
                                    <SettingsRowActions
                                        onEdit={
                                            canEdit
                                                ? () => navigate(`/settings/users/${user._id}`)
                                                : undefined
                                        }
                                        onDelete={
                                            isPending ? () => setInviteToDelete(user) : undefined
                                        }
                                        editLabel="Edit user"
                                        deleteLabel="Delete invitation"
                                    />
                                </SettingsTd>
                            </Tr>
                        );
                    })
                )}
            </SettingsTable>

            <SettingsTableFooter>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    py={0}
                />
            </SettingsTableFooter>

            <SettingsActionBar
                isDirty={dirtyUserIds.length > 0}
                isSaving={isSavingEdits}
                onSave={handleSaveEdits}
                onDiscard={() => setRowDrafts({})}
                message={
                    dirtyUserIds.length > 1
                        ? `Unsaved changes on ${dirtyUserIds.length} users`
                        : undefined
                }
                bottom={selectedUsers.length > 0 ? '76px' : 4}
            />

            <BulkActionBar
                selectedCount={selectedUsers.length}
                actions={BULK_ACTIONS}
                onClearSelection={() => setSelectedUserIds(new Set())}
                onAction={(actionId) => setBulkField(actionId as UsersBulkField)}
            />

            <UsersBulkEditModal
                field={bulkField}
                selectedCount={selectedUsers.length}
                positionOptions={positionOptions}
                locations={locationsData?.locations ?? []}
                isSaving={false}
                onApply={handleBulkApply}
                onClose={() => setBulkField(null)}
            />

            <AlertDialog
                isOpen={inviteToDelete !== null}
                leastDestructiveRef={deleteInviteCancelRef}
                onClose={() => setInviteToDelete(null)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="md">Delete Invitation</AlertDialogHeader>
                        <AlertDialogCloseButton />
                        <AlertDialogBody>
                            <Text fontSize="sm" color="gray.600">
                                This removes the pending invitation for{' '}
                                <Text as="span" fontWeight="semibold" color="gray.800">
                                    {inviteToDelete?.email}
                                </Text>
                                . Their invite link will stop working, but you can re-invite them at any time.
                            </Text>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button
                                size="action"
                                variant="outlineGray"
                                ref={deleteInviteCancelRef}
                                onClick={() => setInviteToDelete(null)}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="action"
                                colorScheme="red"
                                onClick={handleConfirmDeleteInvite}
                                ml={3}
                                isLoading={deleteInviteMutation.isPending}
                                loadingText="Deleting…"
                            >
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default Users;
